import Helmet from 'react-helmet';
import { env } from '../../utils/env';

interface IContent {
  title: string;
  description: string;
  // for dynamic listings
  image?: string;
  price?: string;
  currency?: string;
  prizeImage?: string;
}

const fallbackContent: IContent = {
  title: env.APP_NAME,
  description: env.APP_NAME,
};
export type ContentKey =
  | 'home'
  | 'store'
  | 'about'
  | 'contact'
  | 'terms'
  | 'privacy'
  | 'login'
  | 'register'
  | 'sellerTerms';
const content: Record<ContentKey, IContent> = {
  login: {
    title: `Sign In to ${env.APP_NAME} - Access Your Account`,
    description: `Log in to your ${env.APP_NAME} account to track your sticker orders, view your customer program status, and manage your collector profile.`,
  },
  register: {
    title: `Join ${env.APP_NAME} - Create Your Collector Account`,
    description: `Create your ${env.APP_NAME} account to start collecting exclusive stickers and participate in our customer appreciation program featuring authenticated collectibles.`,
  },
  home: {
    title: `${env.APP_NAME} - Collectible Sticker Shop with Exclusive Rewards`,
    description: `Discover unique collectible stickers at ${env.APP_NAME}. Every purchase automatically enters you into our customer appreciation program featuring curated historical firearms. Shop our transparent, verified marketplace today.`,
  },
  store: {
    title: `Shop Collectible Stickers - ${env.APP_NAME}`,
    description: `Browse our exclusive sticker collection at ${env.APP_NAME}. Each purchase includes automatic entry into our customer appreciation program showcasing authenticated collectible firearms from verified dealers.`,
  },
  sellerTerms: {
    title: `Marketplace Seller Terms - ${env.APP_NAME} Partner Guidelines`,
    description: `Essential guidelines and requirements for verified marketplace sellers at ${env.APP_NAME}. Learn about our authentication process, seller responsibilities, and marketplace standards for collectible documentation.`,
  },
  about: {
    title: `About ${env.APP_NAME} - Our Story & Commitment to Collectors`,
    description: `Learn about ${env.APP_NAME}'s mission to connect sticker enthusiasts with unique collecting opportunities. Discover how we're building a transparent marketplace with verified partners and exclusive customer programs.`,
  },
  contact: {
    title: `Contact ${env.APP_NAME} - Customer Support & Inquiries`,
    description: `Need assistance? Contact our dedicated support team at ${env.SUPPORT_EMAIL}. We're here to help with your sticker orders, customer program questions, and marketplace inquiries.`,
  },
  terms: {
    title: `Terms of Service - ${env.APP_NAME}`,
    description: `Review our comprehensive terms of service covering sticker purchases, customer program participation, and marketplace policies at ${env.APP_NAME}.`,
  },
  privacy: {
    title: `Privacy Policy - ${env.APP_NAME}`,
    description: `Learn how ${env.APP_NAME} protects your privacy and handles data while providing our sticker marketplace and customer appreciation services.`,
  },
};

export const CustomHeader = ({ data }: { data: IContent }) => {
  return (
    <Helmet>
      <title>{data.title}</title>
      <meta name="description" content={data.description} />
      <meta property="og:title" content={data.title} />
      <meta property="og:description" content={data.description} />
      <meta name="twitter:title" content={data.title} />
      <meta name="twitter:description" content={data.description} />

      {data.image && <meta property="og:image" content={data.image} />}
      {data.price && (
        <>
          <meta property="og:type" content="product" />
          <meta property="product:price:amount" content={data.price} />
          <meta
            property="product:price:currency"
            content={data.currency || 'CAD'}
          />
        </>
      )}
      {data.image && <meta property="og:image:width" content="500" />}
      {data.image && <meta property="og:image:height" content="500" />}

      {data.prizeImage && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'Product',
            name: data.title,
            description: data.description,
            image: data.prizeImage,
            offers: {
              '@type': 'Offer',
              price: data.price,
              priceCurrency: data.currency || 'CAD',
              availability: `https://schema.org/InStock`,
              itemCondition: 'https://schema.org/NewCondition',
              description: `Every Sticker Purchase includes automatic entry into a draw for a chance to win${
                data.description ? ': ' + data.description : ''
              }`,
            },
          })}
        </script>
      )}
    </Helmet>
  );
};
const SEOHeader = ({ pageKey }: { pageKey?: ContentKey }) => {
  if (!pageKey) return null;

  const data = content[pageKey] || fallbackContent;

  return <CustomHeader data={data} />;
};

export default SEOHeader;
